import React, { useCallback, useState, useEffect, useRef } from 'react';
import { getDatabase, ref, update } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import PollControls from './managesession/PollControls';
import QuestionDisplay from './managesession/QuestionDisplay';
import CorrectAnswerModal from './managesession/CorrectAnswerModal';
import { 
  subscribeToActiveSession, 
  createSession, 
  endSessionInFirebase, 
  addQuestionToSession, 
  updatePollStateInFirebase, 
  updateCorrectAnswerInFirebase,
  subscribeToPollState,
  subscribeToAttendance,
  subscribeToAnswers
} from './managesession/FirebaseService';
import { createChart } from './managesession/ChartManager';
import usePollTimer from './managesession/UsePollTimer';
import ScreenCaptureControl from './managesession/ScreenCaptureControl';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';

const ManageSession = ({ classId, courseId, theme }) => {
  const [sessionId, setSessionId] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [questionType, setQuestionType] = useState('multiple_choice');
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState([]); // For multiple choices
  const [answersData, setAnswersData] = useState({});
  const [isPollActive, setIsPollActive] = useState(false);
  const [pollStartTime, setPollStartTime] = useState(null);
  const [quizFormat, setQuizFormat] = useState('');
  const [optionCount, setOptionCount] = useState(5);

  // New state variables
  const [attendanceCount, setAttendanceCount] = useState(0);
  const [submittedAnswerCount, setSubmittedAnswerCount] = useState(0);

  // New screen capture states
  const [isCaptureEnabled, setIsCaptureEnabled] = useState(false);
  const [screenStream, setScreenStream] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState('');

  const chartRef = useRef(null);
  const canvasRef = useRef(null);
  const pollStateUnsubscribeRef = useRef(null);
  const attendanceUnsubscribeRef = useRef(null);

  // Use custom hook for poll timer
  const pollTimer = usePollTimer(pollStartTime);

  // Check if screenStream is still active
  const isScreenStreamActive =
    screenStream && screenStream.getTracks().some(track => track.readyState === 'live');
  const disablePollButton = isCaptureEnabled && !isScreenStreamActive;

  const getAnswerLabels = useCallback((type = questionType) => {
    if (type === 'yes_no') return ['Yes', 'No'];
    if (type === 'multiple_choice' || type === 'multiple_multiple_choice') {
      return Array.from({ length: optionCount }, (_, i) => String.fromCharCode(65 + i));
    }
    return [];
  }, [optionCount, questionType]);

  const resetResults = useCallback(() => {
    setAnswersData({});
    setSubmittedAnswerCount(0);

    if (chartRef.current && questionType !== 'short_input') {
      const labels = getAnswerLabels();
      chartRef.current.data.labels = labels;
      chartRef.current.data.datasets[0].data = labels.map(() => 0);
      chartRef.current.update();
    }
  }, [getAnswerLabels, questionType]);

  // Subscribe to active session on mount
  useEffect(() => {
    const unsubscribeSession = subscribeToActiveSession(courseId, (sessions) => {
      let activeSession = null;
      if (sessions) {
        for (let key in sessions) {
          if (!sessions[key].end_date_time) {
            activeSession = { id: key, ...sessions[key] };
            break;
          }
        }
      }
      if (activeSession) {
        setSessionId(activeSession.id);
        setIsSessionActive(true);
        if (pollStateUnsubscribeRef.current) {
          pollStateUnsubscribeRef.current();
        }
        if (attendanceUnsubscribeRef.current) {
          attendanceUnsubscribeRef.current();
        }

        pollStateUnsubscribeRef.current = subscribeToPollState(courseId, activeSession.id, (pollState) => {
          if (pollState) {
            setIsPollActive(pollState.isPollActive);
            setQuestionNumber(pollState.questionNumber);
            setQuestionType(pollState.questionType);
            if (pollState.isPollActive) {
              setPollStartTime(pollState.pollStartTime);
            } else {
              setPollStartTime(null);
            }
          }
        });
        attendanceUnsubscribeRef.current = subscribeToAttendance(classId, activeSession.id, (attendanceData) => {
          setAttendanceCount(attendanceData ? attendanceData.length : 0);
        });
      } else {
        setSessionId(null);
        setIsSessionActive(false);
        setIsPollActive(false);
        setPollStartTime(null);
        setAttendanceCount(0);
        resetResults();
      }
    });

    return () => {
      unsubscribeSession();
      if (pollStateUnsubscribeRef.current) {
        pollStateUnsubscribeRef.current();
        pollStateUnsubscribeRef.current = null;
      }
      if (attendanceUnsubscribeRef.current) {
        attendanceUnsubscribeRef.current();
        attendanceUnsubscribeRef.current = null;
      }
    };
  }, [courseId, classId, resetResults]);

  useEffect(() => {
    if (!classId) return;                       // guard against empty id
    const classDoc = doc(db, 'classes', classId);
    const unsubscribe = onSnapshot(classDoc, (snap) => {
      if (snap.exists()) {
        const qf = snap.data().quizFormat ?? '';
        console.log('quizFormat (from Firestore):', qf);
        setQuizFormat(qf);
      }
    }, (err) => {
      console.error('Error fetching quizFormat:', err);
    });

    return unsubscribe;
  }, [classId]);

  // Subscribe to answers when poll is active
  useEffect(() => {
    let unsubscribe = null;
    if (isSessionActive && isPollActive) {
      resetResults();
      unsubscribe = subscribeToAnswers(courseId, sessionId, questionNumber, (answers) => {
        const counts = {};
        getAnswerLabels(questionType).forEach((label) => {
          counts[label] = 0;
        });

        if (answers) {
          Object.values(answers).forEach((answer) => {
            if (Array.isArray(answer.answer)) {
              answer.answer.forEach((ans) => {
                counts[ans] = (counts[ans] || 0) + 1;
              });
            } else {
              counts[answer.answer] = (counts[answer.answer] || 0) + 1;
            }
          });
          setAnswersData(counts);
          setSubmittedAnswerCount(Object.keys(answers).length);
        } else {
          setAnswersData(counts);
          setSubmittedAnswerCount(0);
        }
      });
    } else {
      resetResults();
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isSessionActive, isPollActive, questionNumber, courseId, sessionId, questionType, getAnswerLabels, resetResults]);

  // Initialize or reinitialize chart when session starts, canvas is available, or theme changes
  useEffect(() => {
    if (isSessionActive && questionType !== 'short_input' && canvasRef.current) {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
      chartRef.current = createChart(canvasRef.current, theme);
    }
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [isSessionActive, questionType, theme]);

  // Update chart when answersData changes
  useEffect(() => {
    if (chartRef.current && answersData && questionType !== 'short_input') {
      chartRef.current.data.labels = Object.keys(answersData);
      chartRef.current.data.datasets[0].data = Object.values(answersData);
      chartRef.current.update();
    }
  }, [answersData, questionType]);

  useEffect(() => {
    if (!isPollActive) {
      resetResults();
    }
  }, [questionNumber, questionType, optionCount, isPollActive, resetResults]);

  const selectWindow = async () => {
    const consent = window.confirm("Do you allow sharing your screen/window?");
    if (!consent) return;
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      setScreenStream(stream);
    } catch (error) {
      console.error("Error capturing screen:", error);
    }
  };

  // Function to capture a screenshot from the selected window
  const captureScreenshot = async () => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.srcObject = screenStream;
      video.play();
      video.onloadedmetadata = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(async (blob) => {
          if (blob) {
            try {
              const photoUrl = await uploadScreenshotToStorage(courseId, sessionId, questionNumber, blob);
              resolve(photoUrl);
            } catch (err) {
              reject(err);
            }
          } else {
            reject(new Error('Failed to capture screenshot'));
          }
        }, 'image/png');
      };
      video.onerror = (err) => reject(err);
    });
  };

  const uploadScreenshotToStorage = async (courseId, sessionId, questionNumber, blob) => {
    const storage = getStorage();
    const screenshotRef = storageRef(storage, `questions/${courseId}/${sessionId}/${questionNumber}/question_image.jpg`);
    await uploadBytes(screenshotRef, blob);
    const url = await getDownloadURL(screenshotRef);
    return url;
  };

  const startSession = async () => {
    try {
      const newSessionId = await createSession(courseId);
      setSessionId(newSessionId);
      setIsSessionActive(true);
      resetResults();
      console.log(`New session created with ID: ${newSessionId}`);
      if (attendanceUnsubscribeRef.current) {
        attendanceUnsubscribeRef.current();
      }
      attendanceUnsubscribeRef.current = subscribeToAttendance(classId, newSessionId, (attendanceData) => {
        setAttendanceCount(attendanceData ? attendanceData.length : 0);
      });
    } catch (error) {
      console.error('Failed to create session:', error.message);
    }
  };

  const endSession = async () => {
    try {
      await endSessionInFirebase(courseId, sessionId);
      setIsSessionActive(false);
      setSessionId(null);
      setIsPollActive(false);
      setPollStartTime(null);
      setAttendanceCount(0);
      resetResults();
      window.location.reload();
      console.log(`Session ${sessionId} for course ${courseId} ended.`);
    } catch (error) {
      console.error('Failed to update session end time:', error.message);
    }
  };

  const addQuestion = async () => {
    try {
      let photoUrl = 'InClass';
      if (isCaptureEnabled) {
        if (!isScreenStreamActive) {
          alert("Selected screen is not available. Please select a valid window.");
          return;
        }
        // Capture screenshot if toggle is enabled and a valid stream exists
        photoUrl = await captureScreenshot();
        setScreenshotPreview(photoUrl);
      }
      await addQuestionToSession(courseId, sessionId, questionNumber, questionType, photoUrl, optionCount);
      console.log(`Question ${questionNumber} added to session ${sessionId}`);

      // Update poll state
      const startedAt = Date.now();
      await updatePollStateInFirebase(courseId, sessionId, {
        isPollActive: true,
        questionNumber: questionNumber,
        questionType: questionType,
        pollStartTime: startedAt,
      });

      // Reset local state for poll
      setIsPollActive(true);
      setPollStartTime(startedAt);
      resetResults();
    } catch (error) {
      console.error('Error adding question to session:', error.message);
    }
  };

  const closePoll = async () => {
    try {
      const database = getDatabase();
      const questionRef = ref(database, `sessions/${courseId}/${sessionId}/questions/${questionNumber}`);
      await update(questionRef, { pool: 'off' });
      console.log(`Poll closed for question ${questionNumber} in session ${sessionId}`);

      // Update poll state
      await updatePollStateInFirebase(courseId, sessionId, {
        isPollActive: false,
        pollStartTime: null,
      });

      setIsPollActive(false);
      setPollStartTime(null);
      resetResults();

      // Show modal for correct answer prompt
      setShowPromptModal(true);
      if (questionType !== 'short_input' && canvasRef.current) {
        if (chartRef.current) {
          chartRef.current.destroy();
          chartRef.current = createChart(canvasRef.current, theme);
        } else {
          chartRef.current = createChart(canvasRef.current, theme);
        }
      }
    } catch (error) {
      console.error('Error updating poll status:', error.message);
    }
  };

  const startPoll = async (e) => {
    e.preventDefault();
    await addQuestion();
  };

  const stopPoll = async (e) => {
    e.preventDefault();
    await closePoll();
  };

  const submitCorrectAnswer = async (e) => {
    e.preventDefault();
    try {
      await updateCorrectAnswerInFirebase(courseId, sessionId, questionNumber, correctAnswer, questionType);
      console.log(`Correct answer for question ${questionNumber} updated in session ${sessionId}`);
      setShowPromptModal(false);
      setCorrectAnswer([]);
      setQuestionNumber(prev => prev + 1);
      resetResults();

      // Update poll state for next question
      await updatePollStateInFirebase(courseId, sessionId, {
        questionNumber: questionNumber + 1,
        questionType,
        isPollActive: false,
        pollStartTime: null,
      });
    } catch (error) {
      console.error('Error updating correct answer:', error.message);
    }
  };

  return (
    <div className="space-y-5 p-4">
      <div
        className="overflow-hidden rounded-lg border"
        style={{
          background: 'linear-gradient(135deg, var(--card-bg) 0%, var(--alt-bg) 100%)',
          borderColor: 'var(--border-color)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <div className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--primary-color)' }}>
              Live classroom
            </p>
            <h3 className="mt-1 text-2xl font-bold" style={{ color: 'var(--text-color)' }}>
              Manage Session
            </h3>
            <p className="mt-2 max-w-2xl text-sm" style={{ color: 'var(--muted-color)' }}>
              Control active polls, capture prompts, and monitor answers as students respond.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg border px-4 py-3" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--card-bg)' }}>
              <div className="text-lg font-bold" style={{ color: 'var(--text-color)' }}>{questionNumber}</div>
              <div className="text-[11px] font-medium uppercase" style={{ color: 'var(--muted-color)' }}>Question</div>
            </div>
            <div className="rounded-lg border px-4 py-3" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--card-bg)' }}>
              <div className="text-lg font-bold" style={{ color: 'var(--text-color)' }}>{attendanceCount}</div>
              <div className="text-[11px] font-medium uppercase" style={{ color: 'var(--muted-color)' }}>Present</div>
            </div>
            <div className="rounded-lg border px-4 py-3" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--card-bg)' }}>
              <div className="text-lg font-bold" style={{ color: isPollActive ? 'var(--success-color)' : 'var(--muted-color)' }}>
                {isPollActive ? 'Live' : isSessionActive ? 'Ready' : 'Off'}
              </div>
              <div className="text-[11px] font-medium uppercase" style={{ color: 'var(--muted-color)' }}>Status</div>
            </div>
          </div>
        </div>
      </div>
      {!isSessionActive ? (
        <button
          type="button"
          className="rounded-lg px-5 py-3 text-sm font-semibold text-white transition-all duration-200"
          style={{ backgroundColor: 'var(--primary-color)', boxShadow: 'var(--shadow-primary)' }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--hover-color)')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--primary-color)')}
          onClick={startSession}
        >
          Start Session
        </button>
      ) : (
        <>
          {/* New Screen Capture Settings */}
          <ScreenCaptureControl
            isCaptureEnabled={isCaptureEnabled}
            setIsCaptureEnabled={setIsCaptureEnabled}
            selectWindow={selectWindow}
            screenshotPreview={screenshotPreview}
            questionNumber={questionNumber}
            setScreenshotPreview={setScreenshotPreview}
          />

          <div className="grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
            <PollControls
              questionType={questionType}
              setQuestionType={setQuestionType}
              isPollActive={isPollActive}
              pollTimer={pollTimer}
              startPoll={startPoll}
              stopPoll={stopPoll}
              showPromptModal={showPromptModal}
              disablePollButton={disablePollButton}
              quizFormat={quizFormat}
              optionCount={optionCount}
              setOptionCount={setOptionCount}
            />
            <QuestionDisplay
              questionNumber={questionNumber}
              submittedAnswerCount={submittedAnswerCount}
              attendanceCount={attendanceCount}
              answersData={answersData}
              questionType={questionType}
              canvasRef={canvasRef}
              isPollActive={isPollActive}
              showPromptModal={showPromptModal}
              pollTimer={pollTimer}
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              className="rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                borderColor: 'var(--border-color)',
                color: 'var(--danger-color)',
                backgroundColor: 'var(--card-bg)',
              }}
              onClick={endSession}
              disabled={isPollActive}
            >
              End Session
            </button>
          </div>
        </>
      )}

      <CorrectAnswerModal
        showPromptModal={showPromptModal}
        questionType={questionType}
        correctAnswer={correctAnswer}
        setCorrectAnswer={setCorrectAnswer}
        submitCorrectAnswer={submitCorrectAnswer}
        optionCount={optionCount}
      />
    </div>
  );
};

export default ManageSession;
